import { Badge, Card, Spinner } from '@fc/ui'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, UsersRound } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { getTeam, listGroups, type GroupVisibility } from '../../lib/teams-groups-api'

const visibilityVariant: Record<GroupVisibility, 'success' | 'gold' | 'neutral'> = {
  public: 'success',
  unlisted: 'gold',
  private: 'neutral',
}

export function TeamDetail() {
  const { id = '' } = useParams()

  const teamQuery = useQuery({ queryKey: ['team', id], queryFn: () => getTeam(id), enabled: !!id })
  const groupsQuery = useQuery({
    queryKey: ['groups', { teamId: id }],
    queryFn: () => listGroups({ teamId: id }),
    enabled: !!id,
  })

  const team = teamQuery.data

  return (
    <div className="space-y-6">
      <Link
        to="/app/teams"
        className="inline-flex items-center gap-1.5 text-sm text-muted transition-colors hover:text-fg"
      >
        <ArrowLeft className="size-4" /> Back to teams
      </Link>

      {teamQuery.isLoading ? (
        <Card>
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading team…
          </div>
        </Card>
      ) : teamQuery.isError || !team ? (
        <Card>
          <div className="py-20 text-center text-sm text-danger">
            {teamQuery.error instanceof Error ? teamQuery.error.message : 'Team not found.'}
          </div>
        </Card>
      ) : (
        <>
          <PageHeader
            title={team.name}
            description={team.siteName ? `Based at ${team.siteName}` : undefined}
            actions={
              <>
                <Badge variant="secondary">
                  {team.groupCount} group{team.groupCount === 1 ? '' : 's'}
                </Badge>
                <Badge variant="neutral">
                  {team.childTeamCount} sub-team{team.childTeamCount === 1 ? '' : 's'}
                </Badge>
              </>
            }
          />

          {team.description && (
            <Card>
              <div className="p-5 text-sm text-muted">{team.description}</div>
            </Card>
          )}

          <div>
            <h2 className="mb-3 font-display text-lg font-semibold tracking-tight">Groups</h2>
            <Card className="p-0">
              {groupsQuery.isLoading ? (
                <div className="flex items-center justify-center gap-2 py-16 text-muted">
                  <Spinner /> Loading groups…
                </div>
              ) : groupsQuery.isError ? (
                <div className="py-16 text-center text-sm text-danger">Couldn’t load groups.</div>
              ) : !groupsQuery.data || groupsQuery.data.length === 0 ? (
                <div className="py-16 text-center text-sm text-muted">
                  This team has no groups yet.
                </div>
              ) : (
                <ul className="divide-y divide-border-soft">
                  {groupsQuery.data.map((g) => (
                    <li key={g.id}>
                      <Link
                        to={`/app/groups/${g.id}`}
                        className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-card-hover focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
                      >
                        <div className="grid size-8 shrink-0 place-items-center rounded-md bg-surface-2 text-secondary">
                          <UsersRound className="size-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-fg">{g.name}</div>
                          <div className="text-xs text-dim">
                            {g.memberCount} member{g.memberCount === 1 ? '' : 's'}
                          </div>
                        </div>
                        <Badge variant={visibilityVariant[g.visibility]} className="capitalize">
                          {g.visibility}
                        </Badge>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  )
}
